import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

/*
 * Plays a recording kept as many short chunks (~5 s webm each) as one stretch
 * of the round's clock. Clips come from the media endpoint as {id, from, to, link}
 * in round seconds.
 *
 * Two <video> (or <audio>) elements take turns: while one plays clip i, the
 * other has clip i+1 loaded, so the switch at a chunk boundary is seamless.
 * Where no clip covers the clock (a gap in the recording), playback jumps to
 * the next clip. A chunk ends at `to - from` seconds or at its own `ended`,
 * whichever comes first: MediaRecorder chunks often report an Infinity
 * duration, so the element's duration is never read.
 *
 * As a driver (the default) it reports its position to `onTime` on timeupdate
 * and on a ~10 Hz animation frame. As a follower (`follow`) it is kept in step
 * with another player by `sync(t)`, waits out its own gaps instead of skipping
 * them, and reports nothing.
 *
 * API (ref): seek(t), play(), pause(), setRate(r), playSegment(from, to), sync(t), position()
 */

const EPS = 0.05;
const GAP = 0.3;
const DRIFT = 0.4;
const FRAME_MS = 100;

/** The clip holding `t`, or the next one after it when `t` is in a gap; -1 past the end. */
export function clipIndexAt(clips, t) {
  for (let i = 0; i < clips.length; i += 1) {
    if (t < clips[i].to - EPS) return { i, gap: t < clips[i].from - EPS };
  }
  return { i: -1, gap: false };
}

/** The stretches between clips, in round seconds (and before the first, when it starts late). */
export function gapsOf(clips, min = 1) {
  const out = [];
  for (let i = 1; i < clips.length; i += 1) {
    const from = clips[i - 1].to;
    const to = clips[i].from;
    if (to - from >= min) out.push({ from, to });
  }
  return out;
}

const ChunkedPlayer = forwardRef(function ChunkedPlayer({
  clips, kind = "video", muted = false, follow = false,
  onTime, onEnded, onError, onWaiting, className = "", label,
}, ref) {
  const refA = useRef(null);
  const refB = useRef(null);
  const els = useMemo(() => [refA, refB], []);
  const slotClip = useRef([-1, -1]);
  const pending = useRef([null, null]);
  const active = useRef(0);
  const [shown, setShown] = useState(0);
  const want = useRef(false);
  const rate = useRef(1);
  const quiet = useRef(false);
  const waiting = useRef(false);
  const stopAt = useRef(null);
  const lastT = useRef(0);
  const list = useRef(clips || []);
  list.current = clips || [];
  const cb = useRef({});
  cb.current = { onTime, onEnded, onError, onWaiting };

  const el = (slot) => els[slot].current;
  const clipOf = (slot) => list.current[slotClip.current[slot]] || null;

  const tell = useCallback((t) => {
    lastT.current = t;
    if (!follow && cb.current.onTime) cb.current.onTime(t);
    if (stopAt.current != null && t >= stopAt.current) {
      stopAt.current = null;
      want.current = false;
      [0, 1].forEach((s) => { const e = el(s); if (e && !e.paused) e.pause(); });
      if (cb.current.onEnded) cb.current.onEnded();
    }
  }, [follow]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPlaying = (slot) => {
    const e = el(slot);
    if (!e) return;
    e.playbackRate = rate.current;
    const p = e.play();
    if (p && typeof p.catch === "function") p.catch(() => { });
  };

  /** Puts clip i on an element at `within` seconds into it. */
  const load = (slot, i, within) => {
    const e = el(slot);
    const clip = list.current[i];
    if (!e || !clip) return;
    if (slotClip.current[slot] !== i || e.getAttribute("src") !== clip.link) {
      slotClip.current[slot] = i;
      pending.current[slot] = within;
      e.setAttribute("src", clip.link);
      e.load();
    } else if (e.readyState >= 1) {
      pending.current[slot] = null;
      try { e.currentTime = within; } catch (err) { pending.current[slot] = within; }
    } else {
      pending.current[slot] = within;
    }
  };

  const show = (slot) => {
    active.current = slot;
    setShown(slot);
  };

  const preloadNext = () => {
    const cur = slotClip.current[active.current];
    const next = cur + 1;
    const other = 1 - active.current;
    if (next < list.current.length && slotClip.current[other] !== next) load(other, next, 0);
  };

  const seek = (t) => {
    const clips_ = list.current;
    lastT.current = t;
    if (!clips_.length) return;
    const { i, gap } = clipIndexAt(clips_, t);
    if (i < 0) {
      /* past the recording: park on the last clip's end */
      waiting.current = false;
      [0, 1].forEach((s) => { const e = el(s); if (e && !e.paused) e.pause(); });
      return;
    }
    const within = gap ? 0 : Math.max(0, t - clips_[i].from);
    const other = 1 - active.current;
    quiet.current = true;
    if (slotClip.current[other] === i && slotClip.current[active.current] !== i) {
      const old = el(active.current);
      if (old && !old.paused) old.pause();
      show(other);
    }
    load(active.current, i, within);
    waiting.current = follow && gap;
    if (want.current && !waiting.current) startPlaying(active.current);
    else { const e = el(active.current); if (e && !e.paused) e.pause(); }
    preloadNext();
  };

  /** Moves on to the next clip: a chunk boundary, or over a gap. */
  const advance = () => {
    const cur = slotClip.current[active.current];
    const next = cur + 1;
    const clips_ = list.current;
    if (next >= clips_.length) {
      want.current = false;
      if (!follow && cb.current.onEnded) cb.current.onEnded();
      return;
    }
    if (follow && clips_[next].from - clips_[cur].to > GAP) {
      /* a follower waits out its own gap until sync() brings the clock to the next clip */
      waiting.current = true;
      const e = el(active.current);
      if (e && !e.paused) e.pause();
      if (cb.current.onWaiting) cb.current.onWaiting(true);
      return;
    }
    const other = 1 - active.current;
    if (slotClip.current[other] !== next) load(other, next, 0);
    else {
      const e = el(other);
      if (e && e.readyState >= 1 && e.currentTime > EPS) { try { e.currentTime = 0; } catch (err) { /* not seekable yet */ } }
    }
    const old = el(active.current);
    show(other);
    if (old && !old.paused) old.pause();
    if (want.current) startPlaying(other);
    tell(clips_[next].from);
    setTimeout(preloadNext, 0);
  };

  const onMeta = (slot) => () => {
    const e = el(slot);
    if (!e) return;
    e.playbackRate = rate.current;
    if (pending.current[slot] != null) {
      const x = pending.current[slot];
      pending.current[slot] = null;
      if (x > EPS || e.currentTime > EPS) { try { e.currentTime = x; } catch (err) { /* keep going */ } }
    }
  };
  const onCanPlay = (slot) => () => {
    onMeta(slot)();
    if (slot === active.current) {
      if (!quiet.current || !el(slot).seeking) quiet.current = false;
      if (want.current && !waiting.current && el(slot).paused) startPlaying(slot);
    }
  };
  const onTimeUpdate = (slot) => () => {
    if (slot !== active.current) return;
    const e = el(slot);
    const clip = clipOf(slot);
    if (!e || !clip) return;
    if (quiet.current) return;
    if (e.currentTime >= clip.to - clip.from - EPS) {
      if (want.current) advance();
      return;
    }
    tell(clip.from + e.currentTime);
  };
  const onSeeked = (slot) => () => {
    if (slot !== active.current) return;
    quiet.current = false;
    const e = el(slot);
    const clip = clipOf(slot);
    if (e && clip) tell(clip.from + e.currentTime);
  };
  const onPlaying = (slot) => () => { if (slot === active.current) quiet.current = false; };
  const onEndedEl = (slot) => () => { if (slot === active.current && want.current) advance(); };
  const onErrorEl = (slot) => () => {
    if (slot !== active.current && slotClip.current[slot] < 0) return;
    if (cb.current.onError) cb.current.onError();
  };

  /* the playhead between timeupdates: ~10 times a second while playing */
  useEffect(() => {
    let raf = 0;
    let last = 0;
    const loop = (ts) => {
      raf = requestAnimationFrame(loop);
      if (ts - last < FRAME_MS) return;
      last = ts;
      if (!want.current || quiet.current || follow) return;
      const e = el(active.current);
      const clip = clipOf(active.current);
      if (e && clip && !e.paused) {
        if (e.currentTime >= clip.to - clip.from - EPS) advance();
        else tell(clip.from + e.currentTime);
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [follow]); // eslint-disable-line react-hooks/exhaustive-deps

  /* fresh links for the same clips (a media refresh): reload where it was */
  const linksKey = (clips || []).map((c) => c.link).join("|");
  const idsKey = (clips || []).map((c) => c.id).join("|");
  const firstLoad = useRef(true);
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    slotClip.current = [-1, -1];
    pending.current = [null, null];
    seek(lastT.current);
  }, [linksKey, idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    want.current = false;
    [0, 1].forEach((s) => {
      const e = el(s);
      if (e) { try { e.pause(); } catch (err) { /* gone */ } }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const api = useMemo(() => ({
    seek: (t) => { stopAt.current = null; seek(t); },
    play: () => {
      want.current = true;
      if (!waiting.current) startPlaying(active.current);
    },
    pause: () => {
      want.current = false;
      stopAt.current = null;
      [0, 1].forEach((s) => { const e = el(s); if (e && !e.paused) e.pause(); });
    },
    setRate: (r) => {
      rate.current = r;
      [0, 1].forEach((s) => { const e = el(s); if (e) e.playbackRate = r; });
    },
    playSegment: (from, to) => {
      seek(from);
      stopAt.current = to;
      want.current = true;
      if (!waiting.current) startPlaying(active.current);
    },
    /** A follower's step: re-seek when it has drifted, wait in a gap, resume at the next clip. */
    sync: (t) => {
      const clips_ = list.current;
      if (!clips_.length) return;
      const { i, gap } = clipIndexAt(clips_, t);
      if (i < 0 || gap) {
        if (!waiting.current) {
          waiting.current = true;
          const e = el(active.current);
          if (e && !e.paused) e.pause();
        }
        return;
      }
      const e = el(active.current);
      const clip = clipOf(active.current);
      const at = e && clip ? clip.from + e.currentTime : NaN;
      if (waiting.current || !Number.isFinite(at) || Math.abs(at - t) > DRIFT || slotClip.current[active.current] !== i) {
        waiting.current = false;
        seek(t);
      } else if (want.current && e.paused) startPlaying(active.current);
    },
    position: () => {
      const e = el(active.current);
      const clip = clipOf(active.current);
      return e && clip ? clip.from + e.currentTime : lastT.current;
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => api, [api]);

  const Tag = kind === "audio" ? "audio" : "video";
  return (
    <div className={`proctoring-review__chunked proctoring-review__chunked--${kind} ${className}`} aria-label={label}>
      {[0, 1].map((slot) => (
        <Tag
          key={slot}
          ref={els[slot]}
          className={`proctoring-review__chunk${shown === slot ? " is-shown" : ""}`}
          preload="auto"
          playsInline
          muted={muted}
          data-slot={slot}
          onLoadedMetadata={onMeta(slot)}
          onCanPlay={onCanPlay(slot)}
          onTimeUpdate={onTimeUpdate(slot)}
          onSeeked={onSeeked(slot)}
          onPlaying={onPlaying(slot)}
          onEnded={onEndedEl(slot)}
          onError={onErrorEl(slot)}
        />
      ))}
    </div>
  );
});

export default ChunkedPlayer;
